import { useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { HeroBanner } from '../components/HeroBanner';
import { KioskFrame } from '../components/KioskFrame';
import { Mascot } from '../components/Mascot';
import { TileCard } from '../components/TileCard';
import { MicIcon, TouchIcon } from '../components/TileIcons';
import { useKiosk } from '../lib/session';

/**
 * Attract Loop（閒置畫面）— Flow 3.1，版面依 2026-08-18 設計稿 p.3／p.4。
 *
 * 主視覺（小獅船長＋三顆對話泡泡）· 三語標題 · 觸碰開始／語音互動兩張大卡。
 * 任何顧客觸碰即進入語言選擇，開始一次新的購票 session。
 *
 * ## 標題三語同時出現
 *
 * 「售票系統 / Ticketing System / チケット販売システム」不隨語系切換——設計稿是
 * 三語並列的標準字組，用意是讓還沒選語言的旅客一眼認出這是什麼機器。切換語系
 * 只會少掉兩種語言的辨識，反而失去這個區塊存在的理由。
 *
 * ## 對話泡泡是示範，不是按鈕
 *
 * 設計稿把它們畫成旁白，示範可以對機器說什麼。做成可點的話，顧客會期待點了就
 * 直接進到那個行程——那是另一套動線，設計稿沒有規範。
 */
export function AttractScreen(): JSX.Element {
  const navigate = useNavigate();
  const { t } = useKiosk();

  // 維運隱藏入口：右下角 1.5 秒內連點 5 次 → 機台設定（device_code 綁定）。
  const tapCount = useRef(0);
  const tapTimer = useRef<number | null>(null);
  function secretTap(): void {
    tapCount.current += 1;
    if (tapTimer.current !== null) window.clearTimeout(tapTimer.current);
    if (tapCount.current >= 5) {
      tapCount.current = 0;
      navigate('/device-setup');
      return;
    }
    tapTimer.current = window.setTimeout(() => {
      tapCount.current = 0;
    }, 1500);
  }

  return (
    <KioskFrame bleed>
      <HeroBanner height={700}>
        <span className="k-bubble k-bubble--plain k-bubble--recommend">
          {t('attract.bubbleRecommend')}
        </span>
        <span className="k-bubble k-bubble--gold k-bubble--cruise">
          {t('attract.bubbleCruise')}
        </span>
        <span className="k-bubble k-bubble--navy k-bubble--water">{t('attract.bubbleWater')}</span>
        <Mascot size={430} pose="video" />
        {/* 設計稿在角色腳邊標了名字，是介紹而非按鈕 */}
        <span className="k-mascotname">{t('attract.mascotName')}</span>
      </HeroBanner>

      <div className="k-titlelock">
        <span className="k-titlelock__rule" aria-hidden="true" />
        <span className="k-titlelock__text">
          <span className="k-titlelock__zh">售票系統</span>
          <span className="k-titlelock__alt">Ticketing System{'　'}チケット販売システム</span>
        </span>
        <span className="k-titlelock__rule" aria-hidden="true" />
      </div>

      <div className="k-tiles k-tiles--2 k-tiles--attract">
        <TileCard
          icon={<TouchIcon />}
          title={t('attract.touchStart')}
          sub={t('attract.touchStartSub')}
          iconHeight={330}
          onClick={() => navigate('/language')}
        />
        <TileCard
          icon={<MicIcon />}
          title={t('attract.voice')}
          sub={t('attract.voiceSub')}
          iconHeight={330}
          onClick={() => navigate('/voice')}
        />
      </div>

      {/* 維運隱藏入口（顧客不可見）：右下角連點 5 次進機台設定 */}
      <div
        onClick={secretTap}
        aria-hidden="true"
        style={{
          position: 'fixed',
          right: 0,
          bottom: 0,
          width: 72,
          height: 72,
          cursor: 'default',
          opacity: 0,
        }}
      />
    </KioskFrame>
  );
}
